<script setup lang="ts">
import { resolveComponent } from 'vue';
import type { THEME } from '@pkg/config/misc/theme';
import { OBJECT } from '@pkg/config/sanity/schemas';
import type { AllButton } from '@pkg/sanity-utils/types/button';
import PortableEditor from '@pkg/portable-text-editor/web/PortableEditor.vue';
import type { PortableTextTextBlock, Image } from '@sanity/types';
import { cn } from '@pkg/ui/lib/utils';
import { useSingleSignUpParameters } from '@pkg/analytics-helper/src/utilities/useSingleSignUpParameter';
import { ButtonGroup } from '../../inner/ButtonGroup';
import TitleBlock from '../../inner/Title/TitleBlock.vue';

const props = defineProps<{
  url: string;
  title: string;
  preTitle: string;
  ctaLabel: string;
  description: string | Array<PortableTextTextBlock>;
  descriptionUnhappy: string | Array<PortableTextTextBlock>;
  image: Image;
  theme: THEME;
}>();

const route = useRoute();

const { locale } = useI18n();
const params = useSingleSignUpParameters(locale.value);
const isReferral:boolean = !!params?.includes('referred_by=');

const ctas = [{
  _type: OBJECT.NAVIGATION_LINK_EXTERNAL,
  title: props?.ctaLabel || 'Shop Now',
  url: `${props?.url}`,
  newWindow: true,
} as AllButton];

const storeName = route?.query?.store_name ? decodeURI(route?.query?.store_name as string) : null;

/** Host app registers these (PartialSection, BaseImage). */
const PartialSection = resolveComponent('PartialSection');
const BaseImage = resolveComponent('BaseImage');
</script>

<template>
  <component :is="PartialSection" :theme="theme">
    <div class="c-referral__wrapper wrapper">
      <div :class="cn('c-referral__content')">
        <slot name="content">
          <TitleBlock
            :pre-title="preTitle"
            :title="title"
            size="large"
            class="mb-24"
          />

          <p v-if="storeName" class="font-bold text-18 mb-8">
            {{ `${storeName} ${$t('referralInvite')}` }}
          </p>

          <div v-if="!isReferral" class="mt-16 mb-24">
            <PortableEditor :content="descriptionUnhappy" class="mb-24" />
            <ButtonGroup :buttons="ctas" />
          </div>

          <div v-if="isReferral" class="c-referral__logo  size--1">
            <img v-if="route?.query?.logo" :src="`${route?.query.logo as string}`" alt="Store logo" class="c-referral__logo-border">
            <img
              v-if="!route?.query?.logo"
              loading="lazy"
              src="~/assets/images/logo.svg"
              alt="Too Good To Go Logo"
            >
          </div>

          <div v-if="isReferral" class="mt-16 mb-24">
            <ButtonGroup :buttons="ctas" />
          </div>

          <div v-if="isReferral" class="text-12 mt-24">
            <PortableEditor :content="description" />
          </div>
        </slot>
      </div>
      <slot name="media">
        <div
          :class="
            cn('c-referral__media-container')"
        >
          <component
            :is="BaseImage"
            :image="image"
            loading="eager"
            :width="792"
            class="c-referral__media"
          />
        </div>
      </slot>
    </div>
  </component>
</template>

<style scoped>

.c-referral {
  &__logo {
  margin-bottom: theme('spacing.24');

    img {
      border-radius: 50%;
      overflow: hidden;
      --size-tablet: 150px;
      --size: 120px;

      width: var(--size);
      height: var(--size);
    }
  }

  &__logo-border {
    border: 4px solid var(--profile-picture-border-color, var(--theme-heading-color));
  }

  &__wrapper {
    display: flex;
    flex-direction: column-reverse;
    justify-content: space-between;
    align-items: center;
    gap: 2rem;
    padding-top: theme('spacing.56');

    @screen desk-small {
      flex-direction: row;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: left;
    width: 100%;
    max-width: 588px;

    &-store-name {
      color: var(--profile-picture-border-color, var(--theme-heading-color));
      text-transform: capitalize;
    }
  }

  &__media {
    object-fit: cover;
    max-height: 220px;
    max-width: min(100%, 588px);
    margin-left: auto;
    margin-right: auto;

    @screen desk-small {
      width: 486px;
      height: 486px;
      max-height: none;
      max-width: unset;
      margin-left: unset;
      margin-right: unset;
    }
  }

}
</style>
